import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Water: an ocean surface receding toward the horizon. A subdivided plane mesh
 * is displaced by a sum of travelling waves + noise chop; per-vertex normals
 * give it specular highlights, a fresnel sky tint and foam on the crests. The
 * swell breathes gently with the music (kept subtle on purpose). Hue tints the
 * water color.
 */
export class Water extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      side: THREE.DoubleSide,
      vertexShader: /* glsl */ `
        attribute vec2 aGrid;      // (u,v) in 0..1 across the plane, for zone mapping
        uniform float uTime, uBass, uBeat, uLevel, uReactivity, uBassPunch;
        varying float vH;
        varying vec3 vNormalW;
        varying vec3 vViewDir;
        varying float vFade;
        varying vec3 vDir;
        ${snoise}
        ${ZONE_GLSL_COMMON}
        float waveH(vec2 p, float t) {
          float h = 0.0;
          h += sin(p.x * 0.35 + t * 1.1) * 0.5;
          h += sin(p.y * 0.27 - t * 0.9) * 0.4;
          h += sin((p.x + p.y) * 0.2 + t * 0.7) * 0.3;
          h += snoise(vec3(p * 0.25, t * 0.2)) * 0.5;   // choppy detail
          return h;
        }
        void main() {
          vec2 p = position.xz;
          float t = uTime;
          // When zone reaction is on, the music nudge is frozen — only zones react.
          float gate = 1.0 - uZoneEnabled;
          // Gentle, mostly steady swell; the music only nudges the amplitude.
          float amp = 0.6 + (uLevel * 0.35 + uBeat * 0.35 * uReactivity) * gate;
          float e = 0.35;
          float h  = waveH(p, t) * amp;
          float hx = waveH(p + vec2(e, 0.0), t) * amp;
          float hz = waveH(p + vec2(0.0, e), t) * amp;
          vec3 nrm = normalize(vec3(h - hx, e, h - hz));
          // Zones ride the surface (u,v) and swell the water locally on their hit.
          vec3 zdir = zoneDirFromUV(aGrid);
          vDir = zdir;
          h += zoneDisp(zdir) * 1.6;
          vH = h;
          vec3 pos = position;
          pos.y += h;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          vNormalW = normalize(normalMatrix * nrm);
          vViewDir = normalize(-mv.xyz);
          float dist = -mv.z;
          vFade = smoothstep(64.0, 36.0, dist) * smoothstep(2.0, 8.0, dist);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uHue, uLevel;
        varying float vH;
        varying vec3 vNormalW;
        varying vec3 vViewDir;
        varying float vFade;
        varying vec3 vDir;
        ${hsv2rgb}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          vec3 nrm = normalize(vNormalW);
          vec3 viewDir = normalize(vViewDir);
          float fres = pow(1.0 - max(dot(nrm, viewDir), 0.0), 3.0);
          vec3 deep = hsv2rgb(vec3(uHue, 0.75, 0.22));
          vec3 shallow = hsv2rgb(vec3(fract(uHue - 0.04), 0.55, 0.6));
          vec3 col = mix(deep, shallow, clamp(vH * 0.5 + 0.5, 0.0, 1.0));
          // Specular glint from a fixed key light.
          vec3 L = normalize(vec3(0.4, 1.0, 0.3));
          float spec = pow(max(dot(reflect(-L, nrm), viewDir), 0.0), 60.0);
          col += spec * (0.8 + uLevel * 0.6);
          // Sky tint at grazing angles + foam on the crests.
          col = mix(col, vec3(0.85, 0.92, 1.0), fres * 0.6);
          float foam = smoothstep(0.85, 1.25, vH);
          col = mix(col, vec3(1.0), foam * 0.5);
          // Zone reaction: the shapes riding the surface tint the water beneath them.
          vec4 zc = zoneColor(vDir);
          col = mix(col, zc.rgb, zc.a);
          gl_FragColor = vec4(col, max(vFade, zc.a * vFade));
        }
      `,
    })
    this.object3D = new THREE.Mesh(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.object3D.position.y = -2.0
    this.rebuild({ density: 1 } as VisualParams)
  }

  rebuild(params: VisualParams): void {
    const d = params.density ?? 1
    const cols = Math.floor(160 * Math.sqrt(d))
    const rows = Math.floor(140 * Math.sqrt(d))
    const width = 44
    const depthNear = 8 // near the camera (z+)
    const depthFar = -64 // toward the horizon (z-)
    const count = cols * rows
    const positions = new Float32Array(count * 3)
    const grid = new Float32Array(count * 2)
    let v = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1)
        const w = r / (rows - 1)
        positions[v * 3 + 0] = (u - 0.5) * width
        positions[v * 3 + 1] = 0
        positions[v * 3 + 2] = depthNear + w * (depthFar - depthNear)
        grid[v * 2 + 0] = u
        grid[v * 2 + 1] = w
        v++
      }
    }
    // Triangulate the grid (two triangles per cell).
    const indices = new Uint32Array((cols - 1) * (rows - 1) * 6)
    let i = 0
    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const a = r * cols + c
        const b = a + 1
        const cc = a + cols
        const dd = cc + 1
        indices[i++] = a
        indices[i++] = cc
        indices[i++] = b
        indices[i++] = b
        indices[i++] = cc
        indices[i++] = dd
      }
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aGrid', new THREE.BufferAttribute(grid, 2))
    geo.setIndex(new THREE.BufferAttribute(indices, 1))
    geo.computeBoundingSphere()
  }
}
